import { useCallback, useRef, useState } from 'react'
import type { ChatIntent, ChatResponse } from '@cypremos/shared'
import { generateClientId } from '@/features/req-guard/utils/ids'
import type { Message } from './ChatPanel'

export interface UseChatControllerOptions<R extends ChatResponse = ChatResponse> {
  /** Posts the full turn history so far and returns the assistant's structured reply. */
  sendTurn: (messages: { role: 'user' | 'assistant'; content: string }[]) => Promise<R>
  onIntent?: (intent: ChatIntent, userMessage: Message) => void
  /** Extra fields for the assistant message built from a reply (e.g. agent-chat blocks). */
  toAssistantExtras?: (response: R) => Partial<Message>
  /** Runs after the assistant message is shown — e.g. executing a returned wizard action. */
  onResponse?: (response: R, assistantMessage: Message, userMessage: Message) => void
  /**
   * Which conversation is shown. Each key keeps its own messages and draft, so separate
   * projects (e.g. the Requirement Doctor and the vECU test generator) do not share a
   * conversation. A reply lands in the conversation it was sent from. Default: one conversation.
   */
  conversationKey?: string
}

/**
 * Owns messages/input/loading state for a ChatPanel mount. Extracted so the identical
 * ChatPanel component can be driven by main-Chat state in one mount and the floating
 * agent chat in another (see docs/chat/phase-1-chat-workflow-design.md).
 */
const EMPTY: Message[] = []

export function useChatController<R extends ChatResponse = ChatResponse>({
  sendTurn,
  onIntent,
  toAssistantExtras,
  onResponse,
  conversationKey = 'default',
}: UseChatControllerOptions<R>) {
  const [conversations, setConversations] = useState<Record<string, Message[]>>({})
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [loadingCounts, setLoadingCounts] = useState<Record<string, number>>({})
  const keyRef = useRef(conversationKey)
  keyRef.current = conversationKey

  const messages = conversations[conversationKey] ?? EMPTY
  const inputValue = drafts[conversationKey] ?? ''
  const loading = (loadingCounts[conversationKey] ?? 0) > 0

  /** Updates one conversation's messages (the current one unless a key is given). */
  const setMessages = useCallback((update: Message[] | ((prev: Message[]) => Message[]), key = keyRef.current) => {
    setConversations((all) => ({ ...all, [key]: typeof update === 'function' ? update(all[key] ?? EMPTY) : update }))
  }, [])

  const setInputValue = useCallback((value: string) => {
    setDrafts((all) => ({ ...all, [keyRef.current]: value }))
  }, [])

  const appendMessage = useCallback((message: Omit<Message, 'id' | 'timestamp'>): Message => {
    const full: Message = { ...message, id: generateClientId(), timestamp: new Date() }
    setMessages((prev) => [...prev, full])
    return full
  }, [setMessages])

  /** Finds the message in whichever conversation holds it. */
  const updateMessage = useCallback((id: string, update: (message: Message) => Message) => {
    setConversations((all) => Object.fromEntries(
      Object.entries(all).map(([key, list]) => [key, list.some((m) => m.id === id) ? list.map((m) => (m.id === id ? update(m) : m)) : list]),
    ))
  }, [])

  const send = useCallback(async () => {
    const trimmed = inputValue.trim()
    if (!trimmed || loading) return

    const userMessage: Message = {
      id: generateClientId(),
      role: 'user',
      content: trimmed,
      timestamp: new Date(),
    }

    const key = conversationKey
    const nextMessages = [...messages, userMessage]
    setMessages(nextMessages, key)
    setInputValue('')
    setLoadingCounts((prev) => ({ ...prev, [key]: (prev[key] ?? 0) + 1 }))

    try {
      const response = await sendTurn(nextMessages.map((m) => ({ role: m.role, content: m.content })))
      const assistantMessage: Message = {
        id: generateClientId(),
        role: 'assistant',
        content: response.message,
        timestamp: new Date(),
        insufficientEvidence: response.insufficientEvidence ?? false,
        ...toAssistantExtras?.(response),
      }
      setMessages((prev) => [...prev, assistantMessage], key)
      onIntent?.(response.intent, userMessage)
      onResponse?.(response, assistantMessage, userMessage)
    } catch (error) {
      console.error('Failed to send message:', error)
      const errorMessage: Message = {
        id: generateClientId(),
        role: 'assistant',
        content: 'Error: Failed to fetch response. Check if the server is running.',
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, errorMessage], key)
    } finally {
      setLoadingCounts((prev) => ({ ...prev, [key]: Math.max(0, (prev[key] ?? 1) - 1) }))
    }
  }, [inputValue, loading, messages, conversationKey, setMessages, setInputValue, sendTurn, onIntent, toAssistantExtras, onResponse])

  return { messages, inputValue, setInputValue, loading, send, appendMessage, updateMessage }
}
